import { env } from './env';

export const REDIRECT_TO_KEY = 'redirectTo';

export type QueryParams = Record<string, string | number | boolean | undefined>;

export type HrefOptions = {
  queryParams?: QueryParams;
  redirectTo?: string;
};

const buildQueryString = (params: QueryParams): string => {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

const handleGetHref = (basePath: string, options?: HrefOptions): string => {
  if (!options) {
    return basePath;
  }

  const { queryParams, redirectTo } = options;

  // Handle redirectTo by adding it to queryParams
  const allParams: QueryParams = { ...queryParams };
  if (redirectTo) {
    allParams[REDIRECT_TO_KEY] = redirectTo;
  }

  // If we have params, build query string
  if (Object.keys(allParams).length > 0) {
    return `${basePath}${buildQueryString(allParams)}`;
  }

  return basePath;
};

type PathConfig = {
  path: string;
  getHref: (options?: HrefOptions) => string;
};

type MaverickPaths = {
  maverick: {
    class: PathConfig & {
      meeting: PathConfig;
      parentMeeting: PathConfig;
    };
  };
};

type BasePaths = {
  auth: {
    root: PathConfig;
    register: PathConfig;
    login: PathConfig;
    forgotPassword: PathConfig;
    resetPassword: PathConfig;
  };
  app: {
    root: PathConfig;
    onboarding: PathConfig;
    modules: PathConfig;
    brainGym: PathConfig;
    moduleReports: PathConfig;
    profiles: PathConfig;
    notifications: PathConfig;
    hobbyMasterClass: PathConfig;
    parentsCommunity: PathConfig;
    sharpenUp: PathConfig;
    videoPlayer: PathConfig;
    batchPausePolicy: PathConfig;
  };
  profile: {
    root: PathConfig;
    select: PathConfig;
  };
  oldApp: {
    assessment: PathConfig;
    quiz: PathConfig;
    quizJoin: PathConfig;
    reportRaw: PathConfig;
    liveSession: PathConfig;
    selectProfile: PathConfig;
  };
};

type Paths = BasePaths & Partial<MaverickPaths>;

const basePaths: BasePaths = {
  auth: {
    root: {
      path: '/auth',
      getHref: (options?: HrefOptions) => handleGetHref('/auth', options),
    },
    register: {
      path: '/auth/register',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/auth/register', options),
    },
    login: {
      path: '/auth/login',
      getHref: (options?: HrefOptions) => handleGetHref('/auth/login', options),
    },
    forgotPassword: {
      path: '/auth/forgot-password',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/auth/forgot-password', options),
    },
    resetPassword: {
      path: '/auth/reset-password',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/auth/reset-password', options),
    },
  },

  app: {
    root: {
      path: '/',
      getHref: (options?: HrefOptions) => handleGetHref('/', options),
    },
    onboarding: {
      path: '/onboarding',
      getHref: (options?: HrefOptions) => handleGetHref('/onboarding', options),
    },
    modules: {
      path: '/modules',
      getHref: (options?: HrefOptions) => handleGetHref('/modules', options),
    },
    brainGym: {
      path: '/brain-gym',
      getHref: (options?: HrefOptions) => handleGetHref('/brain-gym', options),
    },
    moduleReports: {
      path: '/module-reports',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/module-reports', options),
    },
    profiles: {
      path: '/profiles',
      getHref: (options?: HrefOptions) => handleGetHref('/profiles', options),
    },
    notifications: {
      path: '/notifications',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/notifications', options),
    },
    hobbyMasterClass: {
      path: '/hobby-master-class',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/hobby-master-class', options),
    },
    parentsCommunity: {
      path: '/parents-community',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/parents-community', options),
    },
    sharpenUp: {
      path: '/sharpen-up',
      getHref: (options?: HrefOptions) => handleGetHref('/sharpen-up', options),
    },
    videoPlayer: {
      path: '/video',
      getHref: (options?: HrefOptions) => handleGetHref('/video', options),
    },
    batchPausePolicy: {
      path: '/batch-pause-policy',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/batch-pause-policy', options),
    },
  },

  profile: {
    root: {
      path: '/profile',
      getHref: (options?: HrefOptions) => handleGetHref('/profile', options),
    },
    select: {
      path: '/profile/select',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/profile/select', options),
    },
  },

  oldApp: {
    assessment: {
      path: '/assessment/v3/:assessmentId/practice',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/assessment/v3/:assessmentId/practice', options),
    },
    quiz: {
      path: '/quiz/practice/:roomId',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/quiz/practice/:roomId', options),
    },
    quizJoin: {
      path: '/quiz/join/:roomId',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/quiz/join/:roomId', options),
    },
    reportRaw: {
      path: '/report-raw/assessment/v3/:id/solutions',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/report-raw/assessment/v3/:id/solutions', options),
    },
    liveSession: {
      path: '/live-session',
      getHref: () => handleGetHref('/live-session'),
    },
    selectProfile: {
      path: '/account/select-profile',
      getHref: () => handleGetHref('/account/select-profile'),
    },
  },
};

const maverickPaths: MaverickPaths = {
  maverick: {
    class: {
      path: '/maverick/class',
      getHref: (options?: HrefOptions) =>
        handleGetHref('/maverick/class', options),
      meeting: {
        path: '/maverick/class/:meetingId',
        getHref: (options?: HrefOptions) =>
          handleGetHref('/maverick/class/:meetingId', options),
      },
      parentMeeting: {
        path: '/maverick/class/:parentMeetingId/br/:meetingId',
        getHref: (options?: HrefOptions) =>
          handleGetHref(
            '/maverick/class/:parentMeetingId/br/:meetingId',
            options
          ),
      },
    },
  },
};

export const paths: Paths = env.IS_OFFLINE
  ? basePaths
  : { ...basePaths, ...maverickPaths };
